import { useQuery } from "@apollo/client";
import { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Breadcrumb from "../../components/Breadcrumb";
import Card from "../../components/Card";
import {
  EmptyState,
  ErrorBanner,
  ErrorState,
  skeletonTableProps,
} from "../../components/states";
import { useToast } from "../../components/Toast/toastHook";
import { GET_SUB_COMPANIES_BY_SERVICE } from "../../graphql/agentQueries";
import { Table, Button, Modal } from "antd";
import { EyeOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import { formatDate } from "../../utils/dateFormat";

const AgentSubCompanies = () => {
  const [subCompanies, setSubCompanies] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 10 });
  const [totalCount, setTotalCount] = useState(0);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedSubCompany, setSelectedSubCompany] = useState(null);
  const { companyId, serviceId } = useParams();
  const location = useLocation();
  const serviceName = location.state?.serviceName || "Service";
  const navigate = useNavigate();
  const toast = useToast(4000);

  const { loading, error, data, refetch } = useQuery(GET_SUB_COMPANIES_BY_SERVICE, {
    variables: {
      serviceId,
      pagination,
    },
    fetchPolicy: "network-only",
    skip: !serviceId,
  });

  useEffect(() => {
    if (data?.getSubCompanyByServiceID) {
      setSubCompanies(data.getSubCompanyByServiceID.subCompanies || []);
      setTotalCount(data.getSubCompanyByServiceID.totalCount || 0);
    }
  }, [data]);

  useEffect(() => {
    if (error) {
      toast("error", error.message);
    }
  }, [error]);

  const handleTableChange = (paginationConfig) => {
    setPagination({
      page: paginationConfig.current,
      limit: paginationConfig.pageSize,
    });
  };

  const handleViewContacts = (subCompany) => {
    navigate(
      `/company/${companyId}/agent/services/${serviceId}/sub-companies/${subCompany._id}/contacts`,
      {
        state: { serviceName, subCompanyName: subCompany.name },
      }
    );
  };

  const handleViewDetails = (subCompany) => {
    setSelectedSubCompany(subCompany);
    setIsViewModalOpen(true);
  };

  const handleCloseViewModal = () => {
    setIsViewModalOpen(false);
    setSelectedSubCompany(null);
  };

  const handleGoBack = () => {
    navigate(`/company/${companyId}/agent/services`);
  };

  const columns = [
    {
      title: "Company Name",
      dataIndex: "name",
      key: "name",
      render: (name) => <span className="font-medium text-black">{name}</span>,
    },
    {
      title: "Address",
      dataIndex: "address",
      key: "address",
      render: (address) => (
        <span className="text-gray-600 text-sm">
          {address ? (address.length > 50 ? `${address.substring(0, 50)}...` : address) : "-"}
        </span>
      ),
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      render: (phone) => phone || "-",
    },
    {
      title: "Website",
      dataIndex: "website",
      key: "website",
      render: (website) =>
        website ? (
          <a
            href={website}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline"
          >
            Visit
          </a>
        ) : (
          "-"
        ),
    },
    {
      title: "Created Date",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => formatDate(date) || "-",
    },
    {
      title: "Actions",
      key: "actions",
      skeleton: "actions",
      width: 200,
      render: (_, record) => (
        <div className="flex items-center space-x-2">
          <Button
            icon={<EyeOutlined />}
            onClick={() => handleViewDetails(record)}
            title="View Details"
          >
            Details
          </Button>
          <Button
            type="primary"
            className="bg-primary"
            onClick={() => handleViewContacts(record)}
            title="View Contacts"
          >
            Contacts
          </Button>
        </div>
      ),
    },
  ];

  const firstLoad = loading && subCompanies.length === 0;

  return (
    <div id="agent-sub-companies">
      <Breadcrumb pageName={`${serviceName} - Sub Companies`} />
      <div className="pb-6">
        <Card>
          <div className="m-4">
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center space-x-4">
                <Button
                  icon={<ArrowLeftOutlined />}
                  onClick={handleGoBack}
                  className="mr-2"
                >
                  Back
                </Button>
                <span className="font-bold text-lg">
                  Sub Companies ({totalCount})
                </span>
              </div>
            </div>
          </div>
          {error && !subCompanies.length ? (
            <ErrorState onRetry={() => refetch()} />
          ) : (
            <>
              {error && <ErrorBanner onRetry={() => refetch()} />}
              <Table
                bordered
                rowKey="_id"
                onChange={handleTableChange}
                {...(firstLoad
                  ? skeletonTableProps(columns)
                  : {
                      columns,
                      dataSource: subCompanies,
                      loading,
                      pagination: {
                        position: ["bottomCenter"],
                        current: pagination.page,
                        pageSize: pagination.limit,
                        total: totalCount,
                        showSizeChanger: true,
                      },
                    })}
                locale={{
                  emptyText: (
                    <EmptyState
                      title="No sub-companies yet"
                      description="Sub-companies under this service will appear here."
                    />
                  ),
                }}
              />
            </>
          )}
        </Card>
      </div>

      {/* View Sub Company Details Modal */}
      <Modal
        title={selectedSubCompany?.name}
        open={isViewModalOpen}
        onCancel={handleCloseViewModal}
        footer={null}
        width={600}
      >
        {selectedSubCompany && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-gray-500 text-sm">Phone</p>
                <p className="font-medium">{selectedSubCompany.phone || "-"}</p>
              </div>
              <div>
                <p className="text-gray-500 text-sm">Website</p>
                {selectedSubCompany.website ? (
                  <a
                    href={selectedSubCompany.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    {selectedSubCompany.website}
                  </a>
                ) : (
                  <p className="font-medium">-</p>
                )}
              </div>
            </div>
            <div>
              <p className="text-gray-500 text-sm">Address</p>
              <p className="font-medium">{selectedSubCompany.address || "-"}</p>
            </div>
            <div>
              <p className="text-gray-500 text-sm">Service Areas</p>
              <p className="font-medium">{selectedSubCompany.serviceAreas || "-"}</p>
            </div>
            <div>
              <p className="text-gray-500 text-sm">Notes</p>
              <p className="font-medium">{selectedSubCompany.notes || "-"}</p>
            </div>
            <div>
              <p className="text-gray-500 text-sm">Created Date</p>
              <p className="font-medium">{formatDate(selectedSubCompany.createdAt) || "-"}</p>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AgentSubCompanies;
